/* eslint-disable */
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  makeStyles,
  TextField,
  Chip,
  Checkbox
} from '@material-ui/core'
import { Autocomplete } from '@mui/material'
import { Close as CloseIcon, CheckBoxOutlineBlank, CheckBox } from '@material-ui/icons'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'utils/notify'
import ProgressButton from 'components/ProgressButton'
import TipTapEditor from 'components/TipTapEditor/TipTapEditor'
import ErrorMessage from 'components/ErrorMessage'
import { Box } from '@mui/material'
import { isEditorContentEmpty } from 'schemas/setting'
import { cloudFunctionsInstance } from 'config/axios'
import { customEmailsPlaceholders } from 'components/TipTapEditor/placeholders'

const validationSchema = Yup.object().shape({
  recipients: Yup.array()
    .of(Yup.string().email('Invalid email'))
    .min(1, 'At least one recipient is required'),
  subject: Yup.string().required('Subject is required'),
  body: Yup.string()
    .required('Email body is required')
    .test('is-not-empty', 'Email Body cannot be empty', (value) => !isEditorContentEmpty(value))
})

const SendCustomEmailModal = ({
  open,
  onClose,
  selectedRecipients = [],
  availableRecipients = []
}) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const { user } = useSelector((state) => state.auth)

  // Get emails from selected recipients for initial values
  const selectedEmails = selectedRecipients
    .filter((recipient) => recipient?.email)
    .map((recipient) => recipient.email)

  // Create recipient options with email and name for better UX
  const recipientOptions = availableRecipients
    .filter((recipient) => recipient?.email)
    .map((recipient) => ({
      email: recipient.email,
      name: recipient.fullName || `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim(),
      id: recipient.id
    }))

  const initialValues = {
    recipients: selectedEmails,
    subject: '',
    body: ''
  }

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true)
    try {
      await cloudFunctionsInstance.post('/sendCustomEmail', {
        recipients: values.recipients,
        subject: values.subject,
        body: values.body,
        conferenceId: user?.conferenceId
      })

      toast.success('Custom email sent.')
      resetForm()
      onClose()
    } catch (error) {
      console.error('Error:', error)
      const errorMessage = error?.response?.data?.msg || 'Failed to send email. Please try again.'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  return (
    <Dialog open={open} maxWidth="xl" fullWidth PaperProps={{ className: classes.dialogPaper }}>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" component="span">
          Send Custom Email
        </Typography>
        <IconButton className={classes.closeButton} onClick={handleClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize>
        {({ values, setFieldValue, errors, touched, setFieldTouched }) => (
          <Form>
            <DialogContent className={classes.dialogContent}>
              {/* Recipients Field */}
              <Box className={classes.fieldContainer}>
                <Typography className={classes.label}>Recipients</Typography>
                <Autocomplete
                  multiple
                  id="multiple-limit-tags"
                  disableCloseOnSelect
                  limitTags={4}
                  options={recipientOptions}
                  value={recipientOptions.filter((opt) => values.recipients.includes(opt.email))}
                  onChange={(event, newValue) => {
                    const emails = newValue.map((item) => item.email)
                    setFieldValue('recipients', emails)
                  }}
                  onBlur={() => setFieldTouched('recipients', true)}
                  getOptionLabel={(option) => option.email}
                  isOptionEqualToValue={(option, value) => option.email === value.email}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Box className={classes.optionRow}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlank fontSize="small" />}
                          checkedIcon={<CheckBox fontSize="small" />}
                          checked={selected}
                          color="primary"
                          className={classes.optionCheckbox}
                        />
                        <Box className={classes.optionItem}>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography style={{ fontSize: '12px' }} color="textSecondary">
                            {option.email}
                          </Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.email}
                        label={option.email}
                        size="small"
                        variant="outlined"
                        className={classes.emailChip}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        values.recipients.length === 0 ? 'Select email recipients...' : ''
                      }
                      error={!!(touched.recipients && errors.recipients)}
                    />
                  )}
                />
                <ErrorMessage message={touched.recipients && errors.recipients} />
              </Box>

              {/* Subject Field */}
              <Box className={classes.fieldContainer}>
                <Typography className={classes.label}>Subject</Typography>
                <TextField
                  name="subject"
                  value={values.subject}
                  onChange={(e) => setFieldValue('subject', e.target.value)}
                  onBlur={() => setFieldTouched('subject', true)}
                  variant="outlined"
                  fullWidth
                  placeholder="Enter email subject..."
                  error={!!(touched.subject && errors.subject)}
                  helperText={touched.subject && errors.subject}
                />
              </Box>

              {/* Body Field */}
              <Box className={classes.fieldContainer}>
                <Typography className={classes.label}>Email Body</Typography>
                <TipTapEditor
                  value={values.body}
                  onChange={(value) => setFieldValue('body', value)}
                  onBlur={() => setFieldTouched('body', true)}
                  placeholder="Write your email content here..."
                  error={!!(touched.body && errors.body)}
                  minHeight="300px"
                  hasPlaceholders={true}
                  placeholders={customEmailsPlaceholders}
                />
                <ErrorMessage message={touched.body && errors.body} />
              </Box>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
              <Button onClick={handleClose} disabled={loading} variant="outlined">
                Cancel
              </Button>
              <ProgressButton
                type="submit"
                label="Send Email"
                inProgress={loading}
                disabled={loading}
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default SendCustomEmailModal

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: '8px'
  },
  dialogTitle: {
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& h6': {
      fontWeight: 600,
      fontSize: '20px'
    }
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  dialogContent: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5)
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  label: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    marginBottom: '4px'
  },
  emailChip: {
    margin: '2px',
    backgroundColor: '#f5f5f5',
    fontSize: '13px',
    fontWeight: '500',
    padding: '4px 4px 5px 4px',
    height: '24px',
    color: '#494949',
    '& .MuiChip-deleteIcon': {
      color: 'rgba(75,75,75,0.7)'
    }
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginBottom: '4px !important'
  },
  optionCheckbox: {
    padding: '4px',
    marginRight: '8px'
  },
  optionItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  }
}))
